/* src/contexts/AuthContext.js
 * Authentication context for managing user state
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';
import { collectAttribution } from '../utils/attribution';
import { buildSignupAttribution } from '../utils/signupAttribution';
import { authApi } from '../utils/api';

const AuthContext = createContext({});

function resolveSignupAttribution(passedAttribution) {
  if (passedAttribution) return passedAttribution;
  try {
    return collectAttribution();
  } catch {
    return null;
  }
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up with email/password
  async function signup(email, password, passedAttribution) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Branded Postmark verification (masked app.gocta.ai link — never firebaseapp.com)
    await authApi.sendVerificationEmail();

    // Create user profile in Firestore with 14-day trial
    const now = new Date();
    const trialEnd = new Date(now);
    trialEnd.setDate(trialEnd.getDate() + 14);
    
    const attribution = resolveSignupAttribution(passedAttribution);
    const signupAttribution = buildSignupAttribution(attribution);
    
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email: email,
      tier: 'free',
      subscriptionStatus: 'trialing',
      trialActive: true,
      trialStartDate: now,
      trialEndDate: trialEnd,
      submissionLimit: 10,
      submissionsUsed: 0,
      calendarConnected: false,
      isAdmin: false,
      createdAt: now,
      signupAttribution,
      ...(attribution ? { attribution } : {}),
    });
    
    return userCredential;
  }

  // Sign in with email/password
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Sign in with Google
  async function loginWithGoogle(passedAttribution) {
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      
      // Check if user profile exists, create if not
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      const isNewUser = !userDoc.exists();
      if (isNewUser) {
        console.log('[AUTH] Creating new user profile for Google user:', userCredential.user.email);
        try {
          // Create user profile with 14-day trial
          const now = new Date();
          const trialEnd = new Date(now);
          trialEnd.setDate(trialEnd.getDate() + 14);
          
          const attribution = resolveSignupAttribution(passedAttribution);
          const signupAttribution = buildSignupAttribution(attribution);
          
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            email: userCredential.user.email,
            tier: 'free',
            subscriptionStatus: 'trialing',
            trialActive: true,
            trialStartDate: now,
            trialEndDate: trialEnd,
            submissionLimit: 10,
            submissionsUsed: 0,
            calendarConnected: false,
            isAdmin: false,
            createdAt: now,
            signupAttribution,
            ...(attribution ? { attribution } : {}),
          });
          console.log('[AUTH] ✅ User profile created successfully');
        } catch (profileError) {
          console.error('[AUTH] ❌ Error creating user profile:', profileError);
          // Don't throw - user is authenticated, profile creation can be retried
          throw new Error('Account created but profile setup failed. Please try logging in again.');
        }
      } else {
        console.log('[AUTH] ✅ Existing user profile found');
      }
      
      return { userCredential, isNewUser };
    } catch (error) {
      console.error('[AUTH] ❌ Google login error:', error);
      // Provide more helpful error messages
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in popup was closed. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup was blocked. Please allow popups for this site.');
      } else if (error.code === 'auth/network-request-failed') {
        throw new Error('Network error. Please check your connection and try again.');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        throw new Error('An account with this email already exists. Please use email/password login.');
      } else {
        throw new Error(error.message || 'Failed to sign in with Google. Please try again.');
      }
    }
  }

  // Sign out
  function logout() {
    return signOut(auth);
  }

  // Reset password (branded Postmark email with in-app /reset-password link)
  function resetPassword(email) {
    return authApi.sendPasswordResetEmail(email);
  }

  // Load user profile from Firestore
  async function loadUserProfile(uid) {
    if (!uid) {
      setUserProfile(null);
      return;
    }
    
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      setUserProfile(userDoc.data());
    } else {
      setUserProfile(null);
    }
  }

  // Listen for auth state changes and real-time user profile updates (billing, trial, etc.)
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        setUserProfile(null);
        setLoading(false);
      }
    });

    return unsubAuth;
  }, []);

  // Real-time listener for user profile (updates when webhook sets paymentFailedAt, subscriptionStatus, etc.)
  useEffect(() => {
    if (!currentUser?.uid) return;
    setLoading(true);
    const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
      if (snap.exists()) {
        setUserProfile(snap.data());
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    }, (err) => {
      console.error('[AUTH] Profile listener error:', err);
      loadUserProfile(currentUser.uid);
      setLoading(false);
    });
    return () => unsub();
  }, [currentUser?.uid]);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    loadUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
